import { useEffect, useRef } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { toast } from 'sonner'
import { BrandMark } from './BrandMark'
import { UsagePill } from './UsagePill'
import { Tag } from './Tag'
import { cn, PLAN_LABELS } from '@/lib/utils'
import {
  useUsage,
  selectChatPercent,
  selectWeeklyPercent,
  formatWeeklyResetLabel,
} from '@/features/ide/hooks/useUsage'
import { useAuthStore } from '@/stores/auth-store'
import type { Plan } from '@vibe/shared-types'

const TABS = [
  { path: '/', label: '홈' },
  { path: '/onboarding', label: '챗봇' },
  { path: '/ide', label: '작업실' },
  { path: '/archive', label: '친구들 작품' },
] as const

const WARN_THRESHOLD = 80

export interface TopBarProps {
  /** override server data — 스토리북 전용. 비어있으면 실데이터 사용. */
  plan?: Plan
  usagePercent?: number
  initial?: string
  hideOn?: string[]
}

function pickInitial(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    const trimmed = c?.trim()
    if (trimmed) return trimmed.charAt(0).toUpperCase()
  }
  return '?'
}

/**
 * 100,000 → "100K". 1M 이상은 "1M". plan §16 UI "153K / 200K" 표시용.
 */
function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`
  return String(n)
}

export function TopBar({ plan, usagePercent, initial, hideOn = [] }: TopBarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { data } = useUsage()
  const profile = useAuthStore((s) => s.profile)
  const firebaseUser = useAuthStore((s) => s.firebaseUser)

  // 80% 도달 시 1회 토스트
  const warnFiredRef = useRef(false)
  const livePercent = selectChatPercent(data)
  const effectivePercent = usagePercent ?? livePercent
  const effectivePlan: Plan = plan ?? data?.plan ?? 'free'
  // plan v1.3 §16 PR #12 — 주별 토큰 한도. cap 0 (free) 이면 표시 X.
  const weekly = data?.weekly
  const weeklyPercent = selectWeeklyPercent(data)
  const showWeekly = !!weekly && weekly.tokensCap > 0
  const weeklyResetLabel = weekly ? formatWeeklyResetLabel(weekly.msUntilReset) : ''

  useEffect(() => {
    if (effectivePercent >= WARN_THRESHOLD && !warnFiredRef.current) {
      warnFiredRef.current = true
      toast.warning('오늘 사용량 80% 넘었어요', {
        description: '거의 다 썼어요. 자정에 다시 채워져요.',
        duration: 6000,
      })
    }
    if (effectivePercent < WARN_THRESHOLD - 5) {
      warnFiredRef.current = false
    }
  }, [effectivePercent])

  const effectiveInitial =
    initial ??
    pickInitial(profile?.displayName, firebaseUser?.displayName, firebaseUser?.email)

  const displayName =
    profile?.displayName || firebaseUser?.displayName || firebaseUser?.email || '내 계정'

  if (hideOn.some((p) => pathname.startsWith(p))) return null

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex items-center gap-6',
        // V3 애플 글래스 네비(D-05) — 콘텐츠가 아래로 흐르는 반투명 크롬.
        'h-14 px-5 bg-[var(--glass-bg)] backdrop-blur-xl saturate-150',
        'border-b border-border',
      )}
    >
      <Link to="/" className="flex items-center gap-2">
        <BrandMark size={28} />
        <span className="font-bold text-[15px] tracking-tight">Vibetown</span>
        <span className="text-text-3 text-[13px] font-normal">바이브타운</span>
      </Link>

      <nav className="flex items-center gap-1 ml-2" aria-label="주 메뉴">
        {TABS.map(({ path, label }) => {
          const active =
            path === '/' ? pathname === '/' : pathname.startsWith(path)
          return (
            <Link
              key={path}
              to={path}
              className={cn(
                'inline-flex items-center h-8 px-3 rounded-md text-[13px] font-semibold transition-colors',
                active
                  ? 'bg-primary-soft text-primary'
                  : 'text-text-2 hover:bg-surface-alt hover:text-text',
              )}
            >
              {label}
            </Link>
          )
        })}
      </nav>

      <div className="ml-auto flex items-center gap-3">
        <UsagePill percent={effectivePercent} />
        {showWeekly && weekly && (
          <UsagePill
            percent={weeklyPercent}
            label={`이번 주 ${formatTokens(weekly.tokensBilled)} / ${formatTokens(weekly.tokensCap)}`}
            className="hidden md:inline-flex"
          />
        )}
        {showWeekly && weeklyResetLabel && (
          <span className="hidden lg:inline text-[11px] text-text-3">{weeklyResetLabel}</span>
        )}
        <Tag
          variant={
            effectivePlan === 'free'
              ? 'default'
              : effectivePlan === 'basic'
                ? 'primary'
                : 'accent'
          }
        >
          {PLAN_LABELS[effectivePlan]}
        </Tag>
        <span
          className={cn(
            'inline-flex items-center justify-center w-8 h-8 rounded-full',
            'bg-primary-soft text-primary text-[12px] font-bold',
          )}
          aria-label={`내 계정 — ${displayName}`}
          title={displayName}
        >
          {effectiveInitial}
        </span>
      </div>
    </header>
  )
}
